import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Sparkles, X } from 'lucide-react';
import { OPERATORS, targetText } from './model';

// Briques d'interface du module Exploration (même allure que la nouvelle fiche projet : slate / indigo, coins arrondis).
// Les classes de couleur sont écrites en entier (Tailwind ne génère pas les classes composées à la volée).

export const TONES = {
  emerald: { badge: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500', text: 'text-emerald-600', border: 'border-emerald-300' },
  sky: { badge: 'bg-sky-50 text-sky-700 ring-sky-200', dot: 'bg-sky-500', text: 'text-sky-600', border: 'border-slate-200' },
  amber: { badge: 'bg-amber-50 text-amber-800 ring-amber-200', dot: 'bg-amber-500', text: 'text-amber-600', border: 'border-amber-200' },
  rose: { badge: 'bg-rose-50 text-rose-700 ring-rose-200', dot: 'bg-rose-500', text: 'text-rose-600', border: 'border-rose-300' },
  slate: { badge: 'bg-slate-50 text-slate-600 ring-slate-200', dot: 'bg-slate-400', text: 'text-slate-500', border: 'border-slate-200' },
  zinc: { badge: 'bg-zinc-100 text-zinc-500 ring-zinc-200', dot: 'bg-zinc-300', text: 'text-zinc-400', border: 'border-zinc-200' },
  violet: { badge: 'bg-violet-50 text-violet-700 ring-violet-200', dot: 'bg-violet-500', text: 'text-violet-600', border: 'border-violet-200' },
  indigo: { badge: 'bg-indigo-50 text-indigo-700 ring-indigo-200', dot: 'bg-indigo-500', text: 'text-indigo-600', border: 'border-indigo-200' },
  blue: { badge: 'bg-blue-50 text-blue-700 ring-blue-200', dot: 'bg-blue-500', text: 'text-blue-600', border: 'border-blue-200' },
  teal: { badge: 'bg-teal-50 text-teal-700 ring-teal-200', dot: 'bg-teal-500', text: 'text-teal-600', border: 'border-teal-200' },
  orange: { badge: 'bg-orange-50 text-orange-700 ring-orange-200', dot: 'bg-orange-500', text: 'text-orange-600', border: 'border-orange-200' },
  fuchsia: { badge: 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200', dot: 'bg-fuchsia-500', text: 'text-fuchsia-600', border: 'border-fuchsia-200' },
};

export const Chip = ({ tone = 'slate', label, check, className = '', title }) => (
  <span title={title} className={`inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap rounded px-1.5 py-px text-[10.5px] font-semibold ring-1 ring-inset ${(TONES[tone] || TONES.slate).badge} ${className}`}>
    {check && <Check size={11} strokeWidth={3} />}
    {label}
  </span>
);

export const Dot = ({ tone = 'slate', className = '' }) => (
  <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${(TONES[tone] || TONES.slate).dot} ${className}`} />
);

export const Code = ({ children, className = '' }) => (
  <span className={`font-mono text-[10.5px] font-semibold text-slate-500 ${className}`}>{children}</span>
);

export const NexusMark = ({ title = 'Écrit par Nexus', className = '' }) => (
  <span title={title} className={`inline-flex items-center gap-0.5 rounded bg-indigo-50 px-1 text-[9.5px] font-semibold text-indigo-600 ${className}`}>
    <Sparkles size={9} /> Nexus
  </span>
);

export const Button = ({ children, variant = 'secondary', size = 'sm', className = '', icon: Icon, ...props }) => {
  const look = {
    primary: 'bg-slate-900 text-white hover:bg-slate-700',
    indigo: 'bg-indigo-600 text-white hover:bg-indigo-500',
    secondary: 'border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50',
    ghost: 'text-slate-500 hover:bg-slate-100 hover:text-slate-800',
    danger: 'border border-rose-200 bg-white text-rose-700 hover:bg-rose-50',
    nexus: 'border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100',
  }[variant];
  const dim = size === 'xs' ? 'h-7 px-2 text-xs' : 'h-8 px-2.5 text-xs';
  return (
    <button type="button" {...props}
      className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${dim} ${look} ${className}`}>
      {Icon && <Icon size={14} />}
      {children}
    </button>
  );
};

export const Field = ({ label, hint, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
  </label>
);

export const Select = ({ value, onChange, options, placeholder, className = '', disabled = false }) => (
  <div className={`relative ${className}`}>
    <select value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value || null)}
      className="w-full appearance-none rounded-lg border border-slate-200 bg-white py-1.5 pl-2 pr-7 text-sm text-slate-700 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50 disabled:bg-slate-50">
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {Object.entries(options).map(([key, label]) => (
        <option key={key} value={key}>{typeof label === 'string' ? label : label.label}</option>
      ))}
    </select>
    <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
  </div>
);

export const TextInput = ({ value, onCommit, placeholder, className = '', autoFocus = false, onEnter, onType, onFocus }) => {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  const commit = () => { if ((draft ?? '').trim() !== (value ?? '').trim()) onCommit(draft.trim()); };
  return (
    <input value={draft} autoFocus={autoFocus} placeholder={placeholder} onFocus={onFocus}
      onChange={(e) => { setDraft(e.target.value); if (onType) onType(e.target.value); }}
      onBlur={(e) => { commit(); if (onType) onType(null, e); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { commit(); if (onEnter) onEnter(draft.trim()); e.currentTarget.blur(); }
        if (e.key === 'Escape') { setDraft(value ?? ''); e.currentTarget.blur(); }
      }}
      className={`w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 outline-none placeholder:text-slate-300 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50 ${className}`} />
  );
};

export const AreaInput = ({ value, onCommit, placeholder, rows = 3, className = '', onType }) => {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  return (
    <textarea value={draft} rows={rows} placeholder={placeholder} onChange={(e) => { setDraft(e.target.value); if (onType) onType(e.target.value); }}
      onBlur={() => { if (onType) onType(null); if (draft.trim() !== (value ?? '').trim()) onCommit(draft.trim()); }}
      className={`w-full resize-y rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm leading-relaxed text-slate-700 outline-none placeholder:text-slate-300 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50 ${className}`} />
  );
};

/** Cible ou seuil chiffrés : opérateur, valeur (et valeur haute pour « entre »), unité. */
export const TargetInput = ({ value, onCommit, placeholderUnit = 'unité' }) => {
  const [draft, setDraft] = useState(value || { op: '>=', value: '', unit: '' });
  useEffect(() => { setDraft(value || { op: '>=', value: '', unit: '' }); }, [value]);
  const commit = (next) => {
    const v = String(next.value ?? '').trim();
    if (!v) { if (value) onCommit(null); return; }
    const target = { op: next.op, value: v.replace(',', '.'), unit: next.unit || '' };
    if (next.op === 'entre') target.value_max = String(next.value_max ?? '').replace(',', '.');
    if (next.op === 'entre' && !target.value_max) return;
    if (targetText({ ...target, value: Number(target.value), value_max: Number(target.value_max) }) === targetText(value)) return;
    onCommit(target);
  };
  const input = 'rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50';
  return (
    <div className="flex flex-wrap items-center gap-1.5" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) commit(draft); }}>
      <select value={draft.op} onChange={(e) => setDraft({ ...draft, op: e.target.value })} className={`${input} w-16`}>
        {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <input value={draft.value ?? ''} inputMode="decimal" placeholder="valeur" onChange={(e) => setDraft({ ...draft, value: e.target.value })}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(draft); }} className={`${input} w-20`} />
      {draft.op === 'entre' && (
        <>
          <span className="text-xs text-slate-400">et</span>
          <input value={draft.value_max ?? ''} inputMode="decimal" placeholder="max" onChange={(e) => setDraft({ ...draft, value_max: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(draft); }} className={`${input} w-20`} />
        </>
      )}
      <input value={draft.unit ?? ''} placeholder={placeholderUnit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(draft); }} className={`${input} w-20`} />
    </div>
  );
};

export const Toggle = ({ checked, onChange, label, title }) => (
  <label title={title} className="inline-flex cursor-pointer select-none items-center gap-1.5 text-xs font-medium text-slate-600">
    <span className={`relative inline-flex h-4 w-7 items-center rounded-full transition ${checked ? 'bg-indigo-600' : 'bg-slate-300'}`}>
      <span className={`absolute h-3 w-3 rounded-full bg-white shadow transition ${checked ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
    </span>
    <input type="checkbox" className="sr-only" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

export const Drawer = ({ open, onClose, children, width = 'sm:w-[30rem]', title }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <button type="button" aria-label="Fermer" className="absolute inset-0 bg-slate-900/20" onClick={onClose} />
      <aside className={`relative flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl ${width}`}>
        {title && (
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
            <h2 className="min-w-0 truncate text-sm font-semibold text-slate-900">{title}</h2>
            <button type="button" onClick={onClose} aria-label="Fermer" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X size={16} /></button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </aside>
    </div>
  );
};

export const Modal = ({ open, onClose, title, children, footer, width = 'max-w-lg' }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button type="button" aria-label="Fermer" className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div className={`relative w-full ${width} overflow-hidden rounded-2xl bg-white shadow-2xl`}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Fermer" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
};

/** Petit menu déroulant (clic dehors pour fermer). */
export const Menu = ({ trigger, children, align = 'left', className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [open]);
  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      {trigger({ open, toggle: () => setOpen((o) => !o) })}
      {open && (
        <div className={`absolute z-50 mt-1 min-w-[12rem] overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-xl ${align === 'right' ? 'right-0' : 'left-0'}`}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
};

export const MenuItem = ({ icon: Icon, children, onClick, hint }) => (
  <button type="button" onClick={onClick} className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50">
    {Icon && <Icon size={14} className="mt-0.5 shrink-0 text-slate-400" />}
    <span className="min-w-0">
      <span className="block">{children}</span>
      {hint && <span className="block text-[11px] text-slate-400">{hint}</span>}
    </span>
  </button>
);

export const SectionTitle = ({ children, action, className = '' }) => (
  <div className={`mb-2 flex items-center justify-between gap-2 ${className}`}>
    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{children}</h3>
    {action}
  </div>
);

export const Progress = ({ value, tone = 'emerald', className = '' }) => (
  <div className={`h-1.5 overflow-hidden rounded-full bg-slate-100 ${className}`}>
    <div className={`h-full rounded-full ${tone === 'rose' ? 'bg-rose-500' : 'bg-emerald-500'}`} style={{ width: `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%` }} />
  </div>
);

export const Spinner = ({ className = '' }) => (
  <span className={`inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent ${className}`} />
);

export const shortDay = (iso) => (iso ? new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' }).format(new Date(`${String(iso).slice(0, 10)}T12:00:00`)) : '');
export const longDay = (iso) => (iso ? new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${String(iso).slice(0, 10)}T12:00:00`)) : '');
